import { HTMLAttributes, ReactNode, useState } from 'react';

/**
 * NavItem — a sidebar navigation link. Active shows the --nav-active-bg fill
 * with the icon in --accent; inactive is quiet (--text-soft) and lifts to
 * --surface on hover.
 *
 * Renders as an <a> when `href` is set, otherwise a <button>-like div.
 * `size` 'md' (primary nav) or 'sm' (secondary/footer nav).
 */
export interface NavItemProps extends HTMLAttributes<HTMLElement> {
  /** Tabler icon name (without `ti-` prefix), e.g. 'clipboard-list' — or any icon node (e.g. an inline SVG). */
  icon?: string | ReactNode;
  /** Link label (or pass children). */
  label?: ReactNode;
  /** Highlight as the current page. */
  active?: boolean;
  /** Renders an <a> when set. */
  href?: string;
  /** 'md' primary nav (default) or 'sm' secondary/footer nav. */
  size?: 'md' | 'sm';
  /** Trailing --accent notification dot (e.g. unread indicator). */
  dot?: boolean;
  /** Trailing gold count pill (e.g. unread count). Hidden when 0/empty. */
  badge?: string | number;
  onClick?: () => void;
  children?: ReactNode;
}

export function NavItem({ icon, label, active = false, href, onClick, size = 'md', dot = false, badge, style, children, ...props }: NavItemProps) {
  const [hover, setHover] = useState(false);
  const fs = size === 'sm' ? 'var(--fs-nav-secondary)' : 'var(--fs-nav-primary)';
  const iconSize = size === 'sm' ? 14 : 15;

  const base = {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: size === 'sm' ? 'var(--sp-nav-pad-secondary-y) var(--sp-nav-pad-secondary-x)' : 'var(--sp-nav-pad-primary-y) var(--sp-nav-pad-primary-x)',
    borderRadius: 8,
    fontFamily: 'var(--font-sans)',
    fontSize: fs,
    fontWeight: active ? 500 : 400,
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'background .15s, color .15s',
    background: active ? 'var(--nav-active-bg)' : (hover ? 'var(--surface)' : 'transparent'),
    color: active ? 'var(--text)' : (hover ? 'var(--text)' : 'var(--text-soft)'),
    ...style,
  };

  const inner = (
    <>
      {typeof icon === 'string' ? (
        <i
          className={`ti ti-${icon}`}
          style={{ fontSize: iconSize, color: active ? 'var(--accent)' : 'var(--text-dim)' }}
        />
      ) : icon ? (
        <span
          style={{ display: 'inline-flex', flexShrink: 0, color: active ? 'var(--accent)' : 'var(--text-dim)' }}
        >
          {icon}
        </span>
      ) : null}
      {label ?? children}
      {badge != null && String(badge) !== '' && badge !== 0 ? (
        <span
          style={{
            marginLeft: 'auto', flexShrink: 0,
            background: 'rgb(var(--gold))', color: 'rgb(var(--ink))',
            fontSize: 'var(--fs-caption2)', fontWeight: 700, lineHeight: '16px',
            borderRadius: 999, padding: '1px 6px', minWidth: 18, textAlign: 'center',
          }}
        >
          {badge}
        </span>
      ) : dot ? (
        <span style={{ marginLeft: 'auto', width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
      ) : null}
    </>
  );

  const shared = {
    style: base,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
    onClick,
    ...props,
  };

  if (href) {
    return <a className="sk-hit" href={href} {...shared}>{inner}</a>;
  }
  return <div role="button" tabIndex={0} {...shared}>{inner}</div>;
}

export default NavItem;
